// pages/doctor/DoctorConsultation.tsx
// 医師向け問診ページ（三カラムレイアウト）
// 左: 診療録 ｜ 中央: Zoom ｜ 右: 問診票
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MessageSquare, ArrowLeft } from 'lucide-react';
import api from '../../api/axios';
import usePresenceHeartbeat from '../../hooks/usePresenceHeartbeat';
import ZoomEmbed from '../../components/ZoomEmbed';
import DoctorMessageDialog from '../../components/DoctorMessageDialog';
import DoctorRecord from './DoctorRecord';
import DoctorQuestionnaire from './DoctorQuestionnaire';
import DoctorAIAnalysis from './DoctorAIAnalysis';

interface Reservation {
  id: number;
  date: string;
  time: string;
  service: string;
  status: string;
  zoom_join_url: string | null;
  zoom_meeting_id: string | null;
  needs_translation?: boolean;
  user_name?: string;
  user_profile?: { full_name?: string };
}


const DoctorConsultation: React.FC = () => {
  const { reservationId } = useParams<{ reservationId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const rid = Number(reservationId);
  // 医師が診療室にいる間、在席ハートビートを送る（患者側の「医師入室」表示・通知用）
  usePresenceHeartbeat({ source: 'doctor_room' });

  const [reservation, setReservation] = useState<Reservation | null>(null);
  const [doctorName, setDoctorName] = useState<string>('医師');
  const [loading, setLoading] = useState(true);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [aiPrefill, setAiPrefill] = useState<{ s: string; ap: string } | null>(null);
  const [presence, setPresence] = useState<string>('offline');  // 患者在席（在席ランプ）
  const [nowTs, setNowTs] = useState<number>(Date.now());        // 宽限倒计时用の現在時刻ティック
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [resRes, meRes] = await Promise.allSettled([
          api.get<Reservation>(`/doctor/reservations/${rid}`),
          api.get<{ name?: string }>('/doctor/me'),
        ]);
        if (resRes.status === 'fulfilled') setReservation(resRes.value.data);
        if (meRes.status === 'fulfilled' && meRes.value.data.name) {
          setDoctorName(meRes.value.data.name);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [rid]);

  // 患者在席のポーリング + 現在時刻ティック（在席ランプ / 宽限倒计时用）
  useEffect(() => {
    if (!rid) return;
    let stop = false;
    const pollPresence = async () => {
      try {
        const res = await api.get<{ presence: string }>(`/doctor/reservations/${rid}/presence`);
        if (!stop) setPresence(res.data?.presence || 'offline');
      } catch { /* 表示に影響しないため無視 */ }
    };
    pollPresence();
    const pid = window.setInterval(() => { if (!stop) pollPresence(); }, 20000);
    const tid = window.setInterval(() => { if (!stop) setNowTs(Date.now()); }, 15000);
    return () => { stop = true; window.clearInterval(pid); window.clearInterval(tid); };
  }, [rid]);

  const patientName =
    reservation?.user_profile?.full_name ||
    reservation?.user_name ||
    `患者 #${rid}`;

  // 宽限倒计时: 予約開始(JST) + 15分 を過ぎたら「患者未着で終了」を有効化（早すぎるキャンセル防止）
  const GRACE_MINUTES = 15;
  const startTs = (() => {
    if (!reservation?.date || !reservation?.time) return null;
    const tt = reservation.time.length === 5 ? `${reservation.time}:00` : reservation.time;
    const d = new Date(`${reservation.date}T${tt}+09:00`);
    return isNaN(d.getTime()) ? null : d.getTime();
  })();
  const graceDeadline = startTs != null ? startTs + GRACE_MINUTES * 60000 : null;
  const graceElapsed = graceDeadline != null && nowTs >= graceDeadline;
  const minsToGrace = graceDeadline != null ? Math.max(0, Math.ceil((graceDeadline - nowTs) / 60000)) : null;

  const handleNoShow = async () => {
    if (cancelling) return;
    if (!window.confirm('患者の入室が確認できないため、この予約を「患者未着」で終了します。よろしいですか？')) return;
    setCancelling(true);
    try {
      await api.post(`/doctor/cancel_reservation/${rid}`, {
        reason_code: 'patient_no_show',
        reason_text: '患者未着（診療室で確認）',
      });
      alert('「患者未着」で終了しました。患者へ通知を送信しました。');
      navigate('/doctor/reservations');
    } catch (e: any) {
      // サーバの安全ガード（患者入室中など）のメッセージがあれば優先表示
      const detail = e?.response?.data?.detail;
      alert(detail || '処理に失敗しました。もう一度お試しください。');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400">
        読み込み中...
      </div>
    );
  }

  if (!reservation) {
    return <div className="p-6 text-red-500">予約が見つかりません。</div>;
  }

  return (
    <div className="flex flex-col h-screen [height:100dvh] bg-gray-50">
      {/* トップバー */}
      <div className="flex items-center gap-3 px-4 py-2.5 bg-white border-b border-gray-200 shrink-0">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition"
        >
          <ArrowLeft size={15} />
          戻る
        </button>
        <div className="h-4 w-px bg-gray-200" />
        <span className="text-sm font-semibold text-gray-800">{patientName}</span>
        <span className="text-xs text-gray-400">{reservation.date} {reservation.time}</span>
        <span className="ml-auto">
          <button
            onClick={() => setShowMessageDialog(true)}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transition"
          >
            <MessageSquare size={13} />
            {t('consultation.send_message')}
          </button>
        </span>
      </div>

      {/* メインコンテンツ（PC: 3カラム / スマホ: スタック） */}
      <div className="flex flex-1 overflow-hidden md:flex-row flex-col gap-0">

        {/* ── 左カラム: 診療録（直接コンポーネントを描画・iframe 不使用） ── */}
        <div className="md:w-[30%] w-full bg-white border-r border-gray-200 overflow-y-auto">
          <DoctorRecord embeddedId={rid} embedded prefill={aiPrefill} />
        </div>

        {/* ── 中央カラム: Zoom ── */}
        <div className="md:flex-1 w-full bg-gray-900 flex flex-col items-stretch overflow-y-auto">
          <div className="flex items-center justify-between gap-2 px-3 py-2 bg-gray-800">
            <span className="text-xs text-gray-300">{t('consultation.zoom_area')}</span>
            <div className="flex items-center gap-2.5">
              {/* 患者在席ランプ（入室済み=緑 / オンライン未入室=橙 / オフライン=灰） */}
              <span className="flex items-center gap-1.5 text-[11px] text-gray-300">
                <span className={`inline-block w-2 h-2 rounded-full ${presence === 'offline' ? 'bg-gray-500' : presence === 'in_room' ? 'bg-green-500' : 'bg-amber-400'}`} />
                {presence === 'in_room' ? '入室済み' : presence === 'online' ? 'オンライン(未入室)' : '患者オフライン'}
              </span>
              {/* 宽限倒计时 → 過ぎ、かつ患者が未入室のときだけ「患者未着で終了」を表示（誤操作防止） */}
              {graceDeadline != null && (
                !graceElapsed ? (
                  <span className="text-[11px] text-amber-300 whitespace-nowrap">患者未着まで あと{minsToGrace}分</span>
                ) : presence === 'in_room' ? (
                  <span className="text-[11px] text-gray-400 whitespace-nowrap">入室確認済み</span>
                ) : (
                  <button
                    onClick={handleNoShow}
                    disabled={cancelling}
                    className="px-3 py-1 rounded-md bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-[11px] font-semibold"
                  >
                    {cancelling ? '処理中…' : '患者未着で終了'}
                  </button>
                )
              )}
            </div>
          </div>
          <div className="flex-1">
            {reservation.zoom_join_url && reservation.zoom_meeting_id ? (
              <ZoomEmbed
                meetingNumber={reservation.zoom_meeting_id}
                displayName={`${doctorName}（医師）`}
                joinUrl={reservation.zoom_join_url}
                needsTranslation={reservation.needs_translation}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                Zoom リンクが設定されていません
              </div>
            )}
          </div>
        </div>

        {/* ── 右カラム: 問診票 / AI分析 タブ ── */}
        <RightPanel rid={rid} t={t} onApplyToRecord={(s, ap) => setAiPrefill({ s, ap })} />
      </div>

      {/* メッセージダイアログ */}
      {showMessageDialog && (
        <DoctorMessageDialog
          reservationId={rid}
          patientName={patientName}
          onClose={() => setShowMessageDialog(false)}
        />
      )}
    </div>
  );
};

// 右パネル: 問診票 / AI分析 タブ切り替え
const RightPanel: React.FC<{
  rid: number;
  t: (k: string) => string;
  onApplyToRecord: (s: string, ap: string) => void;
}> = ({ rid, t, onApplyToRecord }) => {
  const [tab, setTab] = useState<'questionnaire' | 'ai'>('questionnaire');

  return (
    <div className="md:w-[25%] w-full bg-white border-l border-gray-200 flex flex-col">
      {/* タブヘッダー */}
      <div className="flex border-b border-gray-200 shrink-0">
        <button
          onClick={() => setTab('questionnaire')}
          className={[
            'flex-1 text-xs font-semibold py-2.5 transition-colors',
            tab === 'questionnaire'
              ? 'text-blue-600 border-b-2 border-blue-500'
              : 'text-gray-400 hover:text-gray-600',
          ].join(' ')}
        >
          {t('consultation.questionnaire')}
        </button>
        <button
          onClick={() => setTab('ai')}
          className={[
            'flex-1 text-xs font-semibold py-2.5 transition-colors',
            tab === 'ai'
              ? 'text-blue-600 border-b-2 border-blue-500'
              : 'text-gray-400 hover:text-gray-600',
          ].join(' ')}
        >
          {t('consultation.ai_analysis')}
        </button>
      </div>

      {/* タブコンテンツ */}
      <div className="flex-1 overflow-y-auto">
        {tab === 'questionnaire' ? (
          <DoctorQuestionnaire embeddedId={rid} embedded />
        ) : (
          <DoctorAIAnalysis reservationId={rid} onApplyToRecord={onApplyToRecord} />
        )}
      </div>
    </div>
  );
};

export default DoctorConsultation;
